import { useEffect, useId, useRef } from 'react'
import { useQueryClient, type QueryKey } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { supabase } from '../lib/supabase'

// Generic household-scoped realtime subscription. Each consumer page calls
// this with the tables it cares about plus an invalidation map of
// table → query keys to invalidate when that table fires an event.
//
// Filter defaults to `household_id=eq.${householdId}` since most app tables
// have a household_id column. Pass an object form for tables that filter
// differently — e.g. `households` filters on `id`, since the primary key
// IS the household_id.
//
// One Supabase channel per hook invocation, multiple .on() subscriptions
// on it (matches the pattern Household.tsx used inline before this hook
// existed). The channel name is suffixed with React's useId so two
// components on the same page don't collide on a shared channel name.
//
// `options` is stored in a ref and re-read inside the event callback, so
// the consumer doesn't have to memoize it — the subscription only
// re-creates when householdId actually changes.
//
// Sentry breadcrumbs per event (table + verb + household_id only — never
// the row payload, since payloads can carry sensitive data).

// `filterColumn: null` opts out of postgres_changes column filtering for
// tables that don't have a household_id (or whatever else) on the row —
// e.g. list_items joins to households via list_id, so there's nothing on
// the row to filter on directly. In that case RLS is the only scoping
// boundary (which it already is for everything; the filter is a perf
// optimization, not a security guarantee).
export type RealtimeTable = string | {
  name:          string
  filterColumn?: string | null  // defaults to 'household_id'; pass null for no filter
  filterValue?:  string         // defaults to the householdId arg
}

interface UseHouseholdRealtimeOptions {
  tables:         RealtimeTable[]
  invalidateMap?: Record<string, QueryKey[]>
}

export function useHouseholdRealtime(
  householdId: string | null | undefined,
  options:     UseHouseholdRealtimeOptions,
) {
  const queryClient = useQueryClient()
  const channelKey  = useId()

  const optionsRef = useRef(options)
  optionsRef.current = options

  useEffect(() => {
    if (!householdId) return

    const channel = supabase.channel(`household-rt-${householdId}-${channelKey}`)

    for (const t of optionsRef.current.tables) {
      const name         = typeof t === 'string' ? t : t.name
      const filterColumn = typeof t === 'string' ? 'household_id' : (t.filterColumn === undefined ? 'household_id' : t.filterColumn)
      const filterValue  = typeof t === 'string' ? householdId    : (t.filterValue  ?? householdId)

      const config: {
        event:  '*'
        schema: 'public'
        table:  string
        filter?: string
      } = { event: '*', schema: 'public', table: name }
      if (filterColumn !== null) {
        config.filter = `${filterColumn}=eq.${filterValue}`
      }

      channel.on(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        'postgres_changes' as any,
        config,
        (payload: { eventType: string }) => {
          Sentry.addBreadcrumb({
            category: 'realtime',
            message:  `${name} ${payload.eventType}`,
            level:    'info',
            data:     {
              table:        name,
              event:        payload.eventType,
              household_id: householdId,
            },
          })

          const keys = optionsRef.current.invalidateMap?.[name] ?? []
          for (const key of keys) {
            queryClient.invalidateQueries({ queryKey: key })
          }
        },
      )
    }

    channel.subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [householdId, channelKey, queryClient])
}
